import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { 
  Share2, 
  MessageCircle, 
  Facebook, 
  Twitter, 
  Copy,
  Mail,
  X
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { toast } from 'sonner'

interface ShareButtonProps {
  title: string
  url?: string
  description?: string
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'sm' | 'default' | 'lg'
  className?: string
}

export function ShareButton({ 
  title, 
  url = window.location.href, 
  description = '', 
  variant = 'outline',
  size = 'default',
  className = ''
}: ShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  
  const shareText = description || title
  const encodedTitle = encodeURIComponent(title)
  const encodedUrl = encodeURIComponent(url)
  const encodedText = encodeURIComponent(shareText)

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link berhasil disalin!')
      setIsOpen(false)
    } catch (err) {
      toast.error('Gagal menyalin link')
    }
  }

  const handleNativeShare = async () => {
    if ('share' in navigator) {
      try {
        await navigator.share({
          title: title,
          text: shareText,
          url: url,
        })
        setIsOpen(false)
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          toast.error('Gagal membagikan')
        }
      }
    } else {
      handleCopyLink()
    }
  }

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodedTitle}%0A%0A${encodedText}%0A%0A${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
  }

  const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes')
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <Button 
        variant={variant} 
        size={size} 
        className={className}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Share2 className="h-4 w-4 mr-2" />
        Bagikan
      </Button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md relative">
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-2 top-2 h-8 w-8 p-0"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Bagikan Berita</h3>
              
              <div className="grid grid-cols-2 gap-3">
                {/* Native Share (if supported) */}
                {('share' in navigator) && (
                  <Button
                    variant="outline"
                    className="flex flex-col items-center gap-2 h-auto py-3"
                    onClick={handleNativeShare}
                  >
                    <Share2 className="h-5 w-5 text-gray-600" />
                    <span className="text-sm">Bagikan</span>
                  </Button>
                )}

                {/* WhatsApp */}
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-2 h-auto py-3"
                  onClick={() => openShareWindow(shareLinks.whatsapp)}
                >
                  <MessageCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm">WhatsApp</span>
                </Button>

                {/* Facebook */}
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-2 h-auto py-3"
                  onClick={() => openShareWindow(shareLinks.facebook)}
                >
                  <Facebook className="h-5 w-5 text-blue-600" />
                  <span className="text-sm">Facebook</span>
                </Button>

                {/* Twitter */}
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-2 h-auto py-3"
                  onClick={() => openShareWindow(shareLinks.twitter)}
                >
                  <Twitter className="h-5 w-5 text-sky-500" />
                  <span className="text-sm">Twitter</span>
                </Button>

                {/* Telegram */}
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-2 h-auto py-3"
                  onClick={() => openShareWindow(shareLinks.telegram)}
                >
                  <div className="h-5 w-5 bg-blue-500 rounded-full flex items-center justify-center">
                    <div className="text-white text-xs font-bold">T</div>
                  </div>
                  <span className="text-sm">Telegram</span>
                </Button>

                {/* Email */}
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-2 h-auto py-3"
                  onClick={() => window.location.href = shareLinks.email}
                >
                  <Mail className="h-5 w-5 text-gray-600" />
                  <span className="text-sm">Email</span>
                </Button>

                {/* Copy Link */}
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-2 h-auto py-3"
                  onClick={handleCopyLink}
                >
                  <Copy className="h-5 w-5 text-gray-600" />
                  <span className="text-sm">Salin Link</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default ShareButton